import {useEffect, useRef, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import PageHero from '../components/PageHero';
import DemoLinks from '../components/DemoLinks';

const demos = [
  [
    'Property in Motion',
    'Real Estate Film',
    'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=85'
  ],
  [
    'Sound Without Limits',
    'Studio Production',
    'https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&w=1400&q=85'
  ],
  [
    'The Live Moment',
    'Event Production',
    'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=1400&q=85'
  ],
  [
    'Above Perspective',
    'Aerial Cinematography',
    'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1400&q=85'
  ]
];

function Demos() {
  const [activeDemo, setActiveDemo] = useState(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!activeDemo) return undefined;
    closeButtonRef.current?.focus();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setActiveDemo(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [activeDemo]);

  return (
    <>
      <PageHero
        eyebrow="Selected work"
        title="See what we can create."
        text="A glimpse into the images, sound, energy, and experiences that define our work."
      />
      <section className="demo-grid" aria-label="Selected media projects">
        {demos.map(([title, category, image]) => (
          <motion.article
            key={title}
            className="demo-card"
            initial={{opacity: 0, y: 40}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
          >
            <img src={image} alt="" aria-hidden="true" />
            <div className="demo-overlay">
              <button
                type="button"
                onClick={() => setActiveDemo({title, category, image})}
                aria-label={`Open preview for ${title}, ${category}`}
              >
                <SafeIcon name="Play" aria-hidden="true" focusable="false" />
              </button>
              <span>{category}</span>
              <h2>{title}</h2>
            </div>
          </motion.article>
        ))}
      </section>
      <DemoLinks />
      <AnimatePresence>
        {activeDemo && (
          <motion.div
            className="demo-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-dialog-title"
            aria-describedby="demo-dialog-description"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            onClick={() => setActiveDemo(null)}
          >
            <motion.div
              className="demo-player"
              initial={{scale: 0.85, y: 50}}
              animate={{scale: 1, y: 0}}
              exit={{scale: 0.9, y: 30}}
              onClick={(event) => event.stopPropagation()}
            >
              <img src={activeDemo.image} alt="" aria-hidden="true" />
              <div className="player-shade" aria-hidden="true" />
              <button
                ref={closeButtonRef}
                className="modal-close"
                type="button"
                onClick={() => setActiveDemo(null)}
                aria-label="Close project preview"
              >
                <SafeIcon name="X" aria-hidden="true" focusable="false" />
              </button>
              <div className="player-content">
                <span>{activeDemo.category}</span>
                <h2 id="demo-dialog-title">{activeDemo.title}</h2>
                <p id="demo-dialog-description">
                  Full production preview coming soon.
                </p>
              </div>
              <div className="player-pulse" aria-hidden="true">
                <SafeIcon name="Play" focusable="false" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Demos;